/**
 * @fileoverview Radio button input component
 * @module ctl-radio
 * @version 1.0.0
 * 
 * @description
 * A styled radio button component that wraps a native HTML radio input.
 * Radio buttons allow users to select one option from a group of mutually exclusive choices.
 * Multiple radio buttons with the same name attribute form a radio group where only one
 * can be selected at a time.
 * 
 * The component provides consistent visual design, full keyboard navigation support,
 * proper ARIA attributes, and works seamlessly with standard form behaviors including
 * disabled state, required validation, and form submission with name/value pairs.
 * 
 * @example
 * <!-- Basic radio button group -->
 * <ctl-radio name="size" value="small">Small</ctl-radio>
 * <ctl-radio name="size" value="medium" checked>Medium</ctl-radio>
 * <ctl-radio name="size" value="large">Large</ctl-radio>
 * 
 * @example
 * <!-- Radio group in a form field -->
 * <ctl-form-field label="Choose a size" required>
 *   <div>
 *     <ctl-radio name="size" value="s">S</ctl-radio>
 *     <ctl-radio name="size" value="m">M</ctl-radio>
 *     <ctl-radio name="size" value="l">L</ctl-radio>
 *   </div>
 * </ctl-form-field>
 * 
 * @example
 * <!-- Disabled radio button -->
 * <ctl-radio name="option" value="unavailable" disabled>
 *   Not available
 * </ctl-radio>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2);
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    cursor: pointer;
    user-select: none;
  }

  :host([disabled]) {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .radio-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    pointer-events: none;
  }

  .radio {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--ctl-control-size);
    height: var(--ctl-control-size);
    border: var(--ctl-border-width-thick) solid var(--ctl-color-border);
    border-radius: 50%;
    background: var(--ctl-color-surface);
    transition: all var(--ctl-transition-normal);
    box-sizing: border-box;
  }

  :host(:not([disabled])) .radio-wrapper:hover .radio {
    border-color: var(--ctl-color-focus);
  }

  input[type="radio"]:focus-visible ~ .radio {
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  input[type="radio"]:checked ~ .radio {
    border-color: var(--ctl-color-focus);
  }

  .radio-dot {
    width: var(--ctl-radio-dot-size);
    height: var(--ctl-radio-dot-size);
    border-radius: 50%;
    background: var(--ctl-color-focus);
    opacity: 0;
    transform: scale(0);
    transition: all var(--ctl-transition-normal);
  }

  input[type="radio"]:checked ~ .radio .radio-dot {
    opacity: 1;
    transform: scale(1);
  }

  :host([disabled]) .radio {
    background: color-mix(in srgb, var(--ctl-color-text) 5%, transparent);
    cursor: not-allowed;
  }

  :host([invalid]) .radio {
    border-color: var(--ctl-color-danger);
  }

  label {
    cursor: pointer;
    line-height: 1.4;
    font-size: var(--ctl-font-size-base);
  }

  :host([disabled]) label {
    cursor: not-allowed;
  }
</style>

<div class="radio-wrapper">
  <input
    type="radio"
    id="input"
    part="input"
  />
  <span class="radio" part="radio">
    <span class="radio-dot"></span>
  </span>
</div>
<label for="input" part="label">
  <slot></slot>
</label>
`;

/**
 * Radio Button Web Component
 * 
 * A styled radio input that allows selection of one option from a group.
 * Radio buttons with the same name form a mutually exclusive group.
 * 
 * @class CtlRadio
 * @extends HTMLElement
 * 
 * @attr {boolean} checked - Whether radio button is selected
 * @attr {boolean} disabled - Whether radio button is disabled
 * @attr {boolean} required - Whether radio button is required
 * @attr {boolean} invalid - Whether radio button is in invalid state
 * @attr {string} name - Form field name for grouping (all radios with same name are in same group)
 * @attr {string} value - Value submitted when this radio is selected
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot - Default slot for radio button label text
 * 
 * @fires ctl-change - Custom event with detail.value property
 * 
 * @csspart input - The native radio input element
 * @csspart radio - The custom radio visual element
 * @csspart label - The label element
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted text color (default: #6b7280)
 * @cssprop --ctl-color-border - Border color (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Focus ring and selection color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Error/invalid color (default: #dc2626)
 * @cssprop --ctl-radio-size - Size of the radio button (default: 20px)
 */
export class CtlRadio extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "checked",
      "disabled",
      "required",
      "invalid",
      "name",
      "value",
      "aria-label",
      "aria-labelledby",
      "aria-describedby",
      "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._input = this.shadowRoot.getElementById("input");
    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._upgradeProperty("checked");
    this._upgradeProperty("disabled");
    this._upgradeProperty("value");
    this._upgradeProperty("name");

    this._input.addEventListener("change", this._onChange);
    this.addEventListener("click", this._onClick);
    this._syncToInput();
  }

  disconnectedCallback() {
    this._input.removeEventListener("change", this._onChange);
    this.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._syncToInput(name);
  }

  // --- Properties ---
  get checked() {
    return this.hasAttribute("checked");
  }

  set checked(value) {
    if (value) {
      this.setAttribute("checked", "");
      // Uncheck other radios in the same group
      this._uncheckSiblings();
    } else {
      this.removeAttribute("checked");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }

  set required(value) {
    if (value) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get value() {
    return this.getAttribute("value") || "on";
  }

  set value(val) {
    if (val == null) this.removeAttribute("value");
    else this.setAttribute("value", String(val));
  }

  get name() {
    return this.getAttribute("name");
  }

  set name(val) {
    if (val == null) this.removeAttribute("name");
    else this.setAttribute("name", String(val));
  }

  focus(options) {
    this._input.focus(options);
  }

  blur() {
    this._input.blur();
  }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _syncToInput(changedName) {
    if (!changedName || changedName === "checked") {
      this._input.checked = this.hasAttribute("checked");
    }

    if (!changedName || changedName === "disabled") {
      this._input.disabled = this.hasAttribute("disabled");
    }

    if (!changedName || changedName === "required") {
      this._input.required = this.hasAttribute("required");
    }

    if (!changedName || changedName === "name") {
      const name = this.getAttribute("name");
      if (name) this._input.setAttribute("name", name);
      else this._input.removeAttribute("name");
    }

    if (!changedName || changedName === "value") {
      const value = this.getAttribute("value");
      if (value) this._input.setAttribute("value", value);
      else this._input.removeAttribute("value");
    }

    // ARIA attributes
    const ariaAttrs = ["aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"];
    for (const attr of ariaAttrs) {
      if (!changedName || changedName === attr) {
        if (this.hasAttribute(attr)) {
          this._input.setAttribute(attr, this.getAttribute(attr));
        } else {
          this._input.removeAttribute(attr);
        }
      }
    }

    // Sync aria-invalid based on invalid attribute if not explicitly set
    if (!changedName || changedName === "invalid") {
      if (!this.hasAttribute("aria-invalid")) {
        if (this.hasAttribute("invalid")) {
          this._input.setAttribute("aria-invalid", "true");
        } else {
          this._input.removeAttribute("aria-invalid");
        }
      }
    }
  }

  _onClick(e) {
    // Handle click on host element
    if (this.hasAttribute("disabled")) {
      e.preventDefault();
      return;
    }

    // If click target is not the input itself (e.g., label or host),
    // check the radio programmatically
    if (e.target !== this._input) {
      e.preventDefault();
      this.checked = true;
    }
  }

  _onChange(e) {
    // Sync checked state from native input to component
    if (this._input.checked) {
      this.setAttribute("checked", "");
      this._uncheckSiblings();
    } else {
      this.removeAttribute("checked");
    }

    // Emit custom event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      checked: this._input.checked,
      originalEvent: e
    });
  }

  _uncheckSiblings() {
    const name = this.getAttribute("name");
    if (!name) return;

    // Escape the name attribute to prevent CSS selector injection
    const escapedName =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(name)
        : name.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

    // Find all radio buttons with the same name in the document
    const radios = document.querySelectorAll(`ctl-radio[name="${escapedName}"]`);
    radios.forEach((radio) => {
      if (radio !== this) {
        radio.removeAttribute("checked");
      }
    });
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-radio", CtlRadio);
